import { memo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { CircularProgress, IconButton, Input, Stack, styled, Typography } from "~/common/mui";
import { FileUploadArea } from "./FileUploadArea";
import { QuoteUploadState, type Msg } from "../types/models";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface QuoteUploadSectionProps {
  onFileSelected: (file: File) => void;
  onFileError?: () => void;
  state: QuoteUploadState;
  messages: Msg[];
  cancelUpload: () => void;
}

const QuoteFileUploadSection = memo((props: QuoteUploadSectionProps) => {
  const { onFileSelected, state, cancelUpload } = props;

  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const dragenter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const dragover = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragleave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    onFileSelected(file);
  };

  const drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) {
      return;
    }

    handleFile(file);
  };

  const selectFile = () => {
    inputRef?.current?.click();
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      handleFile(file);
    }
    formRef.current?.reset();
  };

  const btnDisabled = state === QuoteUploadState.UPLOADING;

  let disableInteractions = false;
  let uploading = false;

  if (state === QuoteUploadState.UPLOAD_SUCCESS) {
    uploading = false;
  } else if (state === QuoteUploadState.UPLOADING) {
    disableInteractions = true;
    uploading = true;
  }

  return (
    <>
      <FileDropArea
        onDragEnter={disableInteractions ? undefined : dragenter}
        onDragLeave={disableInteractions ? undefined : dragleave}
        onDragOver={disableInteractions ? undefined : dragover}
        onDrop={disableInteractions ? undefined : drop}
      >
        <form style={{ display: "none" }} ref={formRef}>
          <Input type="file" inputRef={inputRef} onChange={onFileChange} hidden disabled={btnDisabled} />
        </form>
        <FileUploadArea selectFile={selectFile} disabled={btnDisabled} isDragging={isDragging} />
      </FileDropArea>
      {uploading && (
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Stack gap={1} direction={"row"} alignItems={"center"}>
            <Typography component={"b"}>{fileName}</Typography>
            {uploading && <CircularProgress size={16} color="inherit" />}
          </Stack>
          <IconButton onClick={cancelUpload} size={"tiny"}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      )}
    </>
  );
});

export default QuoteFileUploadSection;

const FileDropArea = styled(Stack)({
  width: "100%",
  gap: "8px",
});
